import { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import * as Sentry from "@sentry/react";
import type { AppDispatch, RootState } from "@/store/store";
import userService from "@/endpoints/userService";
import ShowToaster from "@/toaster";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { applyLinkSegmentsLocal, fetchSegments } from "@/store/slices/segments/segments.thunk";
import { MAX_APPLIED_SEGMENTS } from "@/components/projectCommon/Filters/reducer/appliedSegmentsReducer";
import { selectAppliedSegmentCodes } from "@/store/slices/segments/selectors";
import { enterSmartLink, selectSmartLink, setLinkSegments } from "@/store/slices/smartLink/smartLinkSlice";
import { adoptBrandSetIntoStore } from "@/components/BrandSets/shared/adoptBrandSet";
import type { BrandSetView } from "@/components/BrandSets/shared/brandSet.types";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import type { SelectionsRefOrigin } from "@/utils/selections/buildValidateRequest";
import type {
  SelectionsApplyResponse,
  SelectionsErrorBody,
} from "@/interfaces/selections/selections.types";

const errorMessageForCode = (code?: string): string => {
  if (code === "FEATURE_DISABLED") return "Brand Sets isn't enabled for your account.";
  // A 409 means the validate result went stale, so retrying returns 409 forever.
  if (code === "APPLY_CONFLICT") {
    return "Some items are no longer available. Close and reopen the link to see what changed.";
  }
  return "We couldn't add these to your account. Please try again.";
};

async function readErrorBody(err: unknown): Promise<SelectionsErrorBody | null> {
  const response = (err as { response?: { json?: () => Promise<unknown> } })?.response;
  if (typeof response?.json !== "function") return null;
  try {
    return (await response.json()) as SelectionsErrorBody;
  } catch {
    return null;
  }
}

export function useAdoptSelections(): {
  adopt: (
    brandSetUlids: string[],
    segmentCodes: string[],
    origin: SelectionsRefOrigin,
    pathname: string,
  ) => Promise<{ ok: boolean; brandSetMapping: Record<string, string> }>;
  isApplying: boolean;
  errorMessage: string | null;
} {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Hydrate cloned/owned brand-set ENTITIES into the store (selection is owned by
  // ValidateSelections now). Returns source->result for the sets actually hydrated.
  const hydrateAdoptedBrands = async (
    response: SelectionsApplyResponse,
  ): Promise<Record<string, string>> => {
    const mapping: Record<string, string> = {};
    if (!response.brandSets?.length) return mapping;
    const raw = await userService.brandSets.list();
    const list: BrandSetView[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    for (const m of response.brandSets) {
      // Re-read state each iteration: adoptBrandSetIntoStore's
      // setBrandsMappingForMarket dispatch replaces the whole per-market
      // order, so a stale pre-loop snapshot would make each call clobber
      // the previous iteration's write on a multi-set batch.
      const freshState = store.getState();
      const view = list.find((v) => v.ulid === m.result);
      if (view) {
        adoptBrandSetIntoStore(view, {
          dispatch,
          brandsOrderMapping: freshState.activeGroup.brandsOrderMapping,
          brandEntities: freshState.activeGroup.brands.entities,
        });
        mapping[m.source] = m.result;
      }
    }
    return mapping;
  };

  // Hydrates and applies adopted segments. The write already happened by the
  // time this runs, so a throw here is a partial success, not a failed add.
  // Returns the number of adopted result codes the applied-set cap refused.
  const hydrateAdoptedSegments = async (
    response: SelectionsApplyResponse,
    pathname: string,
  ): Promise<number> => {
    if (!response.segments?.length) return 0;

    // Hydrate the cloned segment codes into the catalog before applying them
    // (the catalog gates what applyLinkSegmentsLocal can flip; the listener
    // prunes codes not in the pool).
    // .unwrap() so a catalog-fetch failure throws into phase 2's catch instead
    // of silently leaving hydrateAdopted to run against a stale/empty catalog.
    await dispatch(fetchSegments()).unwrap();
    // screenConfig's slice-level type is `CONFIG_SCHEMA | {}` (empty before any
    // route registers its config), so RootState narrows the field access away —
    // cast through CONFIG_SCHEMA as the rest of the codebase does (appliedSegmentsMirror.ts).
    const mode = (store.getState().screenConfig as CONFIG_SCHEMA | undefined)
      ?.segmentFilterModalMode;
    const resultCodes = response.segments.map((m) => m.result);

    if (mode !== "SEGMENT_ONLY") {
      // REPLACE, matching ValidateSelections' owned-segment restore: the
      // adopted codes are what the recipient just confirmed, not something to
      // merge onto whatever the drawer selection happened to be.
      dispatch(setPendingSelectionIfChanged({ segments: resultCodes }));
      return 0;
    }

    // View-only, same as the owned-segment restore path: extend the active
    // smart-link session's segment override with the newly adopted codes via
    // applyLinkSegmentsLocal rather than persisting through setSegmentApplied
    // / patchPreference. Existing linkSegments keep their slots ahead of new
    // codes when the cap is hit.
    //
    // `response.segments` can legitimately include ALREADY_HAVE/OWNED/ACCESSIBLE
    // codes the caller already has in the session's link set (not just
    // CREATED); the Set dedupes that overlap so a re-adopt of an
    // already-linked code doesn't cost it a slot.
    const smartLink = selectSmartLink(store.getState());
    const existingLinkSegments = smartLink.linkSegments ?? [];
    const newLinkSet = Array.from(
      new Set([...existingLinkSegments, ...resultCodes]),
    ).slice(0, MAX_APPLIED_SEGMENTS);

    if (!smartLink.active) {
      // A link carrying only adoptable segments: Task 6's restore effect
      // found no owned segment to resolve, so no session exists yet — this
      // confirmed adoption is the one that opens it.
      dispatch(
        enterSmartLink({
          snapshotApplied: selectAppliedSegmentCodes(store.getState()),
          linkSegments: newLinkSet,
          enteredPath: pathname,
        }),
      );
    } else {
      dispatch(setLinkSegments(newLinkSet));
    }
    dispatch(applyLinkSegmentsLocal(newLinkSet));

    const fittedCount = resultCodes.filter((code) => newLinkSet.includes(code)).length;
    return resultCodes.length - fittedCount;
  };

  const adopt = async (
    brandSetUlids: string[],
    segmentCodes: string[],
    // origin stays part of the public contract (ValidateSelections owns brand-selection
    // routing with it); this hook no longer needs it.
    origin: SelectionsRefOrigin,
    pathname: string,
  ): Promise<{ ok: boolean; brandSetMapping: Record<string, string> }> => {
    setIsApplying(true);
    setErrorMessage(null);

    // Phase 1 — the write. Only a failure here means nothing was added.
    let response: SelectionsApplyResponse;
    try {
      response = await userService.selections.apply({
        ...(brandSetUlids.length ? { brandSets: brandSetUlids } : {}),
        ...(segmentCodes.length ? { segments: segmentCodes } : {}),
      });
    } catch (err) {
      const body = await readErrorBody(err);
      const code = body?.code;
      // Expected user-facing outcomes don't need Sentry noise; capture everything else.
      if (code !== "FEATURE_DISABLED" && code !== "APPLY_CONFLICT") {
        Sentry.captureException(err, { tags: { feature: "selections", op: "apply" } });
      }
      setErrorMessage(errorMessageForCode(code));
      setIsApplying(false);
      return { ok: false, brandSetMapping: {} };
    }

    // Phase 2 — hydrate entities + segments. The clone already exists server-side,
    // so a failure here is a partial success: report honestly and still close.
    let brandSetMapping: Record<string, string> = {};
    try {
      brandSetMapping = await hydrateAdoptedBrands(response);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "selections", op: "apply-hydrate-brands" } });
    }
    try {
      const cappedSegments = await hydrateAdoptedSegments(response, pathname);
      if (cappedSegments > 0) {
        ShowToaster.warning(
          `Added to your account. ${cappedSegments} ${
            cappedSegments === 1 ? "segment wasn't" : "segments weren't"
          } applied — you can apply up to ${MAX_APPLIED_SEGMENTS} at a time.`,
        );
      } else {
        ShowToaster.success("Added to your account.");
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "selections", op: "apply-hydrate-segments" } });
      ShowToaster.warning(
        "Added to your account, but we couldn't apply them to this view. Reload to use them.",
      );
    } finally {
      setIsApplying(false);
    }
    return { ok: true, brandSetMapping };
  };

  return { adopt, isApplying, errorMessage };
}
